import React, { useEffect, useState } from 'react';
import { Box, Stack, Button, Alert } from '@mui/material';
import { FactCheckOutlined } from '@mui/icons-material';
import { useMsal, useAccount } from '@azure/msal-react';
import { SectionHeader, SectionPaper } from '../../../../common/components/styled-ui-elements';
import { useAppSelector } from '../../../../../app/hooks';
import { useUpdateChecklistItemsMutation } from '../../../../../services/ComplianceService/complianceMutations';
import { ChecklistItemView } from '../../../../../services/ComplianceService/types/complianceTypes';
import { ChecklistItemsList } from '../../ChecklistItems';

/**
 * ChecklistItemsSection - lets a facility manager add a facility-specific checklist item or remove
 * a standard one that doesn't apply there (US3). Reuses `ChecklistItemsList` in its editable
 * add/remove mode; only rendered for EOC items (data-model.md: `ChecklistItems` is empty for every
 * non-EOC requirement).
 */
/** Keeps local checklist-item state in sync with the redux-held compliance item. */
const useSyncedChecklistItems = (source: ChecklistItemView[] | undefined) => {
  const [checklistItems, setChecklistItems] = useState<ChecklistItemView[]>(source ?? []);

  useEffect(() => {
    setChecklistItems(source ?? []);
  }, [source]);

  return [checklistItems, setChecklistItems] as const;
};

/** Saves the current checklist items for one compliance item, tracking loading/error state. */
const useSaveChecklistItems = (
  complianceItemId: string | undefined,
  modifiedBy: string | undefined,
  checklistItems: ChecklistItemView[]
) => {
  const [updateChecklistItems, { isLoading }] = useUpdateChecklistItemsMutation();
  const [errorMessage, setErrorMessage] = useState('');

  const handleSave = async () => {
    if (!complianceItemId) return;

    try {
      setErrorMessage('');
      await updateChecklistItems({ id: complianceItemId, checklistItems, modifiedBy: modifiedBy || 'Unknown User' }).unwrap();
    } catch (error) {
      console.error('Failed to update checklist items:', error);
      setErrorMessage('Failed to save checklist changes. Please try again.');
    }
  };

  return { errorMessage, isLoading, handleSave };
};

/** Encapsulates the section's redux/mutation wiring so the component itself stays declarative. */
const useChecklistItemsForm = () => {
  const { currentComplianceItem } = useAppSelector(state => state.compliance);
  const { accounts } = useMsal();
  const account = useAccount(accounts[0] || {});

  const [checklistItems, setChecklistItems] = useSyncedChecklistItems(currentComplianceItem?.checklistItems);
  const { errorMessage, isLoading, handleSave } = useSaveChecklistItems(
    currentComplianceItem?.id,
    account?.name,
    checklistItems
  );

  return { checklistItems, setChecklistItems, errorMessage, isLoading, handleSave };
};

export const ChecklistItemsSection: React.FC = () => {
  const { checklistItems, setChecklistItems, errorMessage, isLoading, handleSave } = useChecklistItemsForm();

  return (
    <Box>
      <SectionHeader
        icon={<FactCheckOutlined sx={{ mr: 1, color: '#3b82f6', fontSize: 18 }} />}
        title="Checklist Items"
      />
      <SectionPaper>
        <Stack spacing={2}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          <ChecklistItemsList items={checklistItems} onChange={setChecklistItems} editable />
          <Box>
            <Button
              variant="outlined"
              size="small"
              onClick={handleSave}
              disabled={isLoading}
              sx={{ textTransform: 'none' }}
            >
              {isLoading ? 'Saving...' : 'Save Checklist'}
            </Button>
          </Box>
        </Stack>
      </SectionPaper>
    </Box>
  );
};

export default ChecklistItemsSection;
